import { Alert, Button, Checkbox, EmptyState, LoadingState, useToast } from '@orbind/ui'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../../lib/api.ts'
import './herdar-responsavel.css'

/**
 * Preencher responsáveis pela raiz (SPEC-20261103-2144-herdar-responsavel-da-raiz).
 *
 * Itens sem responsável recebem o responsável do Item RAIZ da árvore deles.
 * A tela é em dois passos, sem modal: primeiro o ENSAIO (o que mudaria, por
 * pessoa e por raiz, e as árvores que ficam de fora); depois, com a caixa de
 * confirmação marcada, APLICAR. A regra e a permissão (só quem administra)
 * moram na API — aqui é só o caminho até ela.
 */

type Relatorio = {
  modo: 'ensaio' | 'aplicar'
  aplicado: boolean
  total: number
  abertos: number
  semDonoPulados: number
  porPessoa: { memberId: string; nome: string; total: number; abertos: number }[]
  porRaiz: { rootId: string; code: string; title: string; nome: string; itens: { code: string; title: string; concluido: boolean }[] }[]
  puladas: { rootId: string; code: string; title: string; motivo: 'raiz_sem_responsavel' | 'responsavel_inativo'; semDono: number }[]
}

const MOTIVO: Record<Relatorio['puladas'][number]['motivo'], string> = {
  raiz_sem_responsavel: 'a raiz não tem responsável',
  responsavel_inativo: 'o responsável da raiz não está mais ativo no workspace',
}

async function chamar(modo: 'ensaio' | 'aplicar'): Promise<Relatorio> {
  const r = await api['work-items'].admin['herdar-responsavel-da-raiz'].$post({ json: { modo } })
  const corpo = (await r.json()) as Relatorio & { error?: { message?: string } }
  if (!r.ok) throw new Error(corpo.error?.message ?? 'Não foi possível calcular.')
  return corpo
}

export function HerdarResponsavelSection() {
  const navigate = useNavigate()
  const toast = useToast()
  const [confirmado, setConfirmado] = useState(false)

  const ensaio = useMutation({ mutationFn: () => chamar('ensaio'), onSuccess: () => setConfirmado(false) })
  const aplicar = useMutation({
    mutationFn: () => chamar('aplicar'),
    onSuccess: (r) => {
      setConfirmado(false)
      toast.push({ tone: 'success', message: `${r.total} Item(ns) receberam o responsável da raiz.` })
    },
    onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível aplicar.' }),
  })

  const resultado = aplicar.data ?? ensaio.data
  const carregando = ensaio.isPending || aplicar.isPending

  return (
    <div className="hrr">
      <header className="hrr__head">
        <h2 className="hrr__title">Preencher responsáveis pela raiz</h2>
        <p className="hrr__sub">
          Itens de Trabalho sem responsável recebem o responsável do Item do topo da árvore deles. Quem já tem
          responsável não muda; arquivados e templates ficam de fora. Cada pessoa recebe um aviso por árvore, não um
          por Item. Primeiro veja o que mudaria; nada é gravado até você aplicar.
        </p>
        <div className="hrr__acoes">
          <Button variant="primary" icon="fas fa-search" disabled={carregando} onClick={() => ensaio.mutate()}>
            {resultado ? 'Calcular de novo' : 'Ver o que mudaria'}
          </Button>
        </div>
      </header>

      {carregando ? (
        <LoadingState variant="detail" label={aplicar.isPending ? 'Aplicando' : 'Calculando'} />
      ) : ensaio.isError && !resultado ? (
        <Alert tone="danger" title="Não foi possível calcular">
          {ensaio.error instanceof Error ? ensaio.error.message : 'Tente novamente em instantes.'}
        </Alert>
      ) : !resultado ? null : (
        <>
          {resultado.aplicado ? (
            <Alert tone="success" title="Aplicado">
              {resultado.total} Item(ns) receberam o responsável da raiz ({resultado.abertos} em aberto). O histórico de
              cada Item registra a mudança, e as pessoas foram avisadas no sino.
            </Alert>
          ) : null}

          <dl className="hrr__resumo">
            <div>
              <dt>{resultado.aplicado ? 'Receberam responsável' : 'Vão receber responsável'}</dt>
              <dd>{resultado.total}</dd>
            </div>
            <div>
              <dt>Em aberto entre eles</dt>
              <dd>{resultado.abertos}</dd>
            </div>
            <div>
              <dt>Ficam sem dono (árvores puladas)</dt>
              <dd>{resultado.semDonoPulados}</dd>
            </div>
          </dl>

          {resultado.total === 0 && resultado.puladas.length === 0 ? (
            <EmptyState icon="fas fa-check-circle" title="Nada a preencher" description="Todo Item de Trabalho vivo já tem responsável." />
          ) : null}

          {resultado.porPessoa.length > 0 ? (
            <section className="hrr__bloco" aria-label="Por pessoa">
              <h3 className="hrr__h3">Por pessoa</h3>
              <table className="hrr__tabela">
                <thead>
                  <tr>
                    <th>Pessoa</th>
                    <th>Itens</th>
                    <th>Em aberto</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.porPessoa.map((p) => (
                    <tr key={p.memberId}>
                      <td>{p.nome}</td>
                      <td>{p.total}</td>
                      <td>{p.abertos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {resultado.porRaiz.length > 0 ? (
            <section className="hrr__bloco" aria-label="Por raiz">
              <h3 className="hrr__h3">Por árvore ({resultado.porRaiz.length})</h3>
              <ul className="hrr__raizes">
                {resultado.porRaiz.map((r) => (
                  <li key={r.rootId}>
                    <details className="hrr__raiz">
                      <summary>
                        <span className="hrr__codigo">{r.code}</span>
                        <span className="hrr__titulo">{r.title}</span>
                        <span className="hrr__meta">
                          {r.itens.length} Item(ns) → {r.nome}
                        </span>
                      </summary>
                      <ul className="hrr__itens">
                        {r.itens.map((i) => (
                          <li key={i.code}>
                            <span className="hrr__codigo">{i.code}</span> {i.title}
                            {i.concluido ? <span className="hrr__concluido"> · concluído</span> : null}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {resultado.puladas.length > 0 ? (
            <section className="hrr__bloco" aria-label="Árvores puladas">
              <h3 className="hrr__h3">Ficam de fora ({resultado.puladas.length})</h3>
              <p className="hrr__nota">
                Nessas árvores não há de quem herdar. Defina o responsável da raiz e calcule de novo, ou distribua os
                Itens à mão.
              </p>
              <ul className="hrr__puladas">
                {resultado.puladas.map((p) => (
                  <li key={p.rootId}>
                    <button type="button" className="hrr__link" onClick={() => navigate(`/work-items/${p.rootId}`)}>
                      <span className="hrr__codigo">{p.code}</span> {p.title}
                    </button>
                    <span className="hrr__meta">
                      {p.semDono} sem dono · {MOTIVO[p.motivo]}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {!resultado.aplicado && resultado.total > 0 ? (
            <section className="hrr__bloco hrr__confirmar" aria-label="Aplicar">
              <Checkbox
                label={`Confirmo preencher ${resultado.total} Item(ns) com o responsável da raiz.`}
                checked={confirmado}
                onChange={(e) => setConfirmado(e.target.checked)}
              />
              <div>
                <Button variant="primary" icon="fas fa-check" disabled={!confirmado || carregando} onClick={() => aplicar.mutate()}>
                  Aplicar
                </Button>
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  )
}
